import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from './icons';
import axios from 'axios';
import { tx } from './i18n';
import { useWebSerialRig, rigSendCw, rigStopCw } from './webSerialCat';

const API = process.env.REACT_APP_API_URL || '';

// ── Super Check Partial (jak w N1MM) ─────────────────────────────────────────
// Po wpisaniu fragmentu znaku pokazuje pasujace znaki: zielone -- z Twojego
// logu (pracowane wczesniej), pozostale -- z bazy MASTER.SCP (stacje aktywne
// w zawodach; wgrywa ja admin). '?' = dowolny znak, '*' = dowolny ciag.
export function ScpSuggestions({ value, onPick }) {
  const [res, setRes] = useState(null);
  useEffect(() => {
    const q = (value || '').trim();
    if (q.replace(/[?*.]/g, '').length < 2) { setRes(null); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/api/scp`, { params: { q } }).then(r => setRes(r.data)).catch(() => setRes(null));
    }, 180);
    return () => clearTimeout(t);
  }, [value]);
  if (!res) return null;
  const exact = (value || '').trim().toUpperCase();
  return (
    <div className="scp">
      <span className="scp__label">SCP</span>
      {res.log.map(c => (
        <button key={'l' + c} type="button" className={'scp__call scp__call--log' + (c === exact ? ' scp__call--exact' : '')}
          title={tx('Już w Twoim logu')} onClick={() => onPick(c)}>{c}</button>
      ))}
      {res.master.map(c => (
        <button key={'m' + c} type="button" className={'scp__call' + (c === exact ? ' scp__call--exact' : '')}
          onClick={() => onPick(c)}>{c}</button>
      ))}
      {res.log.length === 0 && res.master.length === 0 && <span className="scp__none">{tx('brak pasujących')}</span>}
      {!res.master_loaded && <span className="scp__none">{tx('(baza MASTER.SCP nie jest wgrana — Panel admina → Kopie zapasowe)')}</span>}
    </div>
  );
}

// ── Klucz glosowy i CW (jak w N1MM / Log4OM) ─────────────────────────────────
// 6 komunikatow pod klawiszami F7-F12. Emisja fonia: odtwarza nagranie
// (nagrywasz je tutaj mikrofonem) na wybrane wyjscie audio -- podlacz je do
// wejscia mikrofonowego/USB radia (VOX albo PTT z radia). Emisja CW: tekst
// idzie do wbudowanego keyera radia przez agenta CAT (Hamlib send_morse).
// Nagrania zostaja w TEJ przegladarce (IndexedDB), nie na serwerze.
const KEYS = ['F7', 'F8', 'F9', 'F10', 'F11', 'F12'];
const DEFAULT_SLOTS = [
  { label: 'CQ', cw: 'CQ TEST {MYCALL} {MYCALL} TEST' },
  { label: tx('Raport'), cw: '{CALL} 5NN {NR}' },
  { label: 'TU', cw: 'TU {MYCALL}' },
  { label: tx('Znak'), cw: '{MYCALL}' },
  { label: tx('Powtórz'), cw: 'AGN?' },
  { label: 'QRZ?', cw: 'QRZ?' },
];
const DB_NAME = 'hamlogger_keyer';

function idb(mode, fn) {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(DB_NAME, 1);
    open.onupgradeneeded = () => open.result.createObjectStore('clips');
    open.onerror = () => reject(open.error);
    open.onsuccess = () => {
      const tx = open.result.transaction('clips', mode);
      const req = fn(tx.objectStore('clips'));
      tx.oncomplete = () => resolve(req?.result);
      tx.onerror = () => reject(tx.error);
    };
  });
}
const clipGet = (i) => idb('readonly', s => s.get(i));
const clipPut = (i, blob) => idb('readwrite', s => s.put(blob, i));
const clipDel = (i) => idb('readwrite', s => s.delete(i));

function expandMacros(text, ctx) {
  return (text || '')
    .replace(/\{MYCALL\}/g, ctx.mycall || '')
    .replace(/\{CALL\}/g, ctx.call || '')
    .replace(/\{NR\}/g, ctx.nr || '')
    .replace(/\{RST\}/g, ctx.rst || '599')
    .replace(/\s+/g, ' ').trim().toUpperCase();
}

export function KeyerPanel({ mode, ctx, notify, compact }) {
  const [slots, setSlots] = useState(() => {
    try { return JSON.parse(localStorage.getItem('keyer_slots')) || DEFAULT_SLOTS; } catch { return DEFAULT_SLOTS; }
  });
  const [clips, setClips] = useState({});          // i -> true (jest nagranie)
  const [recording, setRecording] = useState(null); // indeks nagrywanego slotu
  const [playing, setPlaying] = useState(null);
  const [outputs, setOutputs] = useState([]);
  const [output, setOutput] = useState(() => { try { return localStorage.getItem('keyer_output') || ''; } catch { return ''; } });
  const [editing, setEditing] = useState(false);
  const recRef = useRef(null);
  const audioRef = useRef(null);
  const isCw = (mode || '').toUpperCase() === 'CW';
  // Radio podlaczone przez USB w przegladarce: tekst CW idzie prosto do radia (po wlaczeniu zgody na nadawanie)
  const rigOn = useWebSerialRig().status === 'connected';

  useEffect(() => { try { localStorage.setItem('keyer_slots', JSON.stringify(slots)); } catch { /* brak storage */ } }, [slots]);
  useEffect(() => { try { localStorage.setItem('keyer_output', output); } catch { /* brak storage */ } }, [output]);
  useEffect(() => {
    Promise.all(KEYS.map((_, i) => clipGet(i).then(b => [i, !!b]).catch(() => [i, false])))
      .then(list => setClips(Object.fromEntries(list)));
  }, []);
  const loadOutputs = useCallback(() => {
    navigator.mediaDevices?.enumerateDevices?.().then(devs => setOutputs(devs.filter(d => d.kind === 'audiooutput'))).catch(() => {});
  }, []);
  useEffect(() => { loadOutputs(); }, [loadOutputs]);

  const stop = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlaying(null);
  }, []);

  const fire = useCallback(async (i) => {
    const slot = slots[i];
    if (!slot) return;
    const hasClip = clips[i];
    if (!isCw && hasClip) {
      stop();
      const blob = await clipGet(i).catch(() => null);
      if (!blob) return;
      const a = new Audio(URL.createObjectURL(blob));
      if (output && a.setSinkId) { try { await a.setSinkId(output); } catch { notify(tx('Nie można użyć wybranego wyjścia audio'), 'error'); } }
      audioRef.current = a;
      setPlaying(i);
      a.onended = () => { setPlaying(null); URL.revokeObjectURL(a.src); };
      a.play().catch(() => setPlaying(null));
      return;
    }
    const text = expandMacros(slot.cw, ctx);
    if (!text) { notify(isCw ? tx('Ten klawisz nie ma tekstu CW') : tx('Brak nagrania dla tego klawisza'), 'warning'); return; }
    try {
      if (rigOn) await rigSendCw(text);
      else await axios.post(`${API}/api/cat/cw`, { text });
      notify(`📡 CW: ${text}`, 'success', 1500);
    } catch (err) { notify(err.response?.data?.error || err.message || tx('Błąd wysyłki CW'), 'error'); }
  }, [slots, clips, isCw, ctx, output, notify, stop, rigOn]);

  // F7-F12 = komunikaty, Esc = stop odtwarzania
  useEffect(() => {
    const onKey = (e) => {
      const idx = KEYS.indexOf(e.key);
      if (idx >= 0) { e.preventDefault(); fire(idx); }
      else if (e.key === 'Escape') { if (audioRef.current) stop(); if (rigOn) rigStopCw().catch(() => {}); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fire, stop, rigOn]);

  const record = async (i) => {
    if (recording === i) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks = [];
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = e => chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
        await clipPut(i, blob).catch(() => notify(tx('Nie udało się zapisać nagrania'), 'error'));
        setClips(c => ({ ...c, [i]: true }));
        setRecording(null);
        loadOutputs();   // po zgodzie na mikrofon przegladarka pokazuje nazwy urzadzen
      };
      recRef.current = rec;
      rec.start();
      setRecording(i);
    } catch { notify(tx('Brak dostępu do mikrofonu'), 'error'); }
  };

  return (
    <div className={'keyer' + (compact ? ' keyer--compact' : '')}>
      <div className="keyer__keys">
        {slots.map((s, i) => (
          <button key={i} type="button" className={'keyer__key' + (playing === i ? ' keyer__key--on' : '')} onClick={() => fire(i)}
            title={isCw ? expandMacros(s.cw, ctx) : (clips[i] ? tx('Odtwórz nagranie') : tx('Brak nagrania'))}>
            <span className="keyer__fn">{KEYS[i]}</span> {s.label}
            {!isCw && <span className="keyer__dot">{clips[i] ? '🎙️' : '—'}</span>}
          </button>
        ))}
        {playing !== null && <button type="button" className="btn btn--ghost btn--sm" onClick={stop}>{tx('⏹ Stop (Esc)')}</button>}
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditing(v => !v)}>{editing ? tx('Zamknij') : <><Icon name="settings" size={16} /> {tx('Ustaw')}</>}</button>
      </div>
      <div className="keyer__hint">
        {isCw ? tx('📡 CW przez keyer radia (agent CAT)') : tx('🎙️ Fonia: nagrania na wybrane wyjście audio')} {tx('· makra:')} {'{MYCALL} {CALL} {NR} {RST}'}
      </div>
      {editing && (
        <div className="panel-box" style={{ marginTop: 8 }}>
          <div className="form-group"><label>{tx('Wyjście audio (podłączone do radia)')}</label>
            <select className="input" value={output} onChange={e => setOutput(e.target.value)}>
              <option value="">{tx('Domyślne wyjście systemu')}</option>
              {outputs.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || tx('Urządzenie {0}', [d.deviceId.slice(0, 6)])}</option>)}
            </select>
          </div>
          {slots.map((s, i) => (
            <div key={i} className="form-row" style={{ alignItems: 'flex-end' }}>
              <div className="form-group" style={{ maxWidth: 70 }}><label>{KEYS[i]}</label>
                <input className="input" value={s.label} onChange={e => setSlots(sl => sl.map((x, j) => j === i ? { ...x, label: e.target.value.slice(0, 12) } : x))} /></div>
              <div className="form-group form-group--lg"><label>{tx('Tekst CW')}</label>
                <input className="input" style={{ fontFamily: 'var(--mono)' }} value={s.cw}
                  onChange={e => setSlots(sl => sl.map((x, j) => j === i ? { ...x, cw: e.target.value.toUpperCase().slice(0, 60) } : x))} /></div>
              <button type="button" className={'btn btn--sm ' + (recording === i ? 'btn--primary' : 'btn--ghost')} onClick={() => record(i)}>
                {recording === i ? tx('⏺ Zatrzymaj') : tx('🎙️ Nagraj')}</button>
              {clips[i] && <button type="button" className="btn-icon btn-icon--danger" title={tx('Usuń nagranie')}
                onClick={async () => { await clipDel(i).catch(() => {}); setClips(c => ({ ...c, [i]: false })); }}><Icon name="trash" size={16} /></button>}
            </div>
          ))}
          <p style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.6, margin: 0 }}>
            {tx('Fonia: wyjście audio komputera → wejście audio radia (np. interfejs USB); nadawanie przez VOX albo PTT. CW: agent HamLoggera wysyła tekst do rigctld (Hamlib „send_morse”) — działa z radiami, których sterownik to obsługuje (m.in. wiele ICOM i Kenwood). Nagrania zostają w tej przeglądarce.')}
          </p>
        </div>
      )}
    </div>
  );
}

// Samodzielny widok (Narzedzia) -- do przygotowania nagran i testu klucza.
export default function KeyerView({ notify, userProfile }) {
  const [mode, setMode] = useState('SSB');
  const [call, setCall] = useState('');
  return (
    <section className="section">
      <div className="section__header">
        <h2><Icon name="megaphone" size={20} /> {tx('Klucz głosowy i CW')}</h2>
        <select className="input" style={{ width: 'auto' }} value={mode} onChange={e => setMode(e.target.value)}>
          <option value="SSB">{tx('Fonia (nagrania)')}</option>
          <option value="CW">{tx('CW (keyer radia)')}</option>
        </select>
      </div>
      <div style={{ padding: 16 }}>
        <p style={{ fontSize: 13, color: 'var(--text-dim)', marginTop: 0 }}>
          {tx('Te same klawisze F7–F12 działają w trybie zawodów loggera. Tutaj nagrasz komunikaty i sprawdzisz, czy trafiają do radia.')}
        </p>
        <div className="form-group" style={{ maxWidth: 220 }}><label>{tx('Znak korespondenta (do makra')} {'{CALL}'})</label>
          <input className="input" style={{ fontFamily: 'var(--mono)' }} value={call} onChange={e => setCall(e.target.value.toUpperCase())} /></div>
        <KeyerPanel mode={mode} ctx={{ mycall: userProfile?.callsign, call, nr: '001' }} notify={notify} />
      </div>
    </section>
  );
}
